/* eslint-disable @typescript-eslint/no-explicit-any */
import { useAuthState } from '../components/AuthState';
import { useSeason } from '../components/SeasonState';

type UseFetchOptions = { expectedType?: string };

type ReqInit = Expand<
  Omit<RequestInit, 'method' | 'body'> & {
    method?: 'GET' | 'PUT' | 'POST' | 'DELETE';
    body?: unknown;
  }
>;
/**
 * This implementation differs from the built-in fetch() in several ways. This
 * one does the following:
 * - If a body is present set content-type to be application/json
 * - If a body is present and no method specified default to POST
 * - If a body is present, stringify it using JSON.stringify()
 * - Throw if the response status is not OK
 * - Throw if the response content-type is not application/json
 * - Automatically parse the response as JSON and return that
 */
export async function fetchJson<T = any>(
  url: string | URL,
  init: ReqInit = {},
) {
  const response = await fetch(toRequest(url, init));
  checkResponseStatus(response);
  checkResponseType(response, 'application/json');
  const data = await response.json();
  return data as T;
}

export function useFetchJson() {
  const addHeaders = useAddHeaders();
  return async <T = any>(
    url: string | URL,
    init?: ReqInit | null,
    responseTransform?: (data: any) => T,
  ): Promise<T> => {
    const headers = addHeaders(init?.headers);
    const data = await fetchJson(url, { ...init, headers });
    return responseTransform ? responseTransform(data) : data;
  };
}

export function useFetch(options: UseFetchOptions = {}) {
  const addHeaders = useAddHeaders();
  return async (url: string | URL, init?: ReqInit) => {
    const headers = addHeaders(init?.headers);
    const response = await fetch(toRequest(url, { ...init, headers }));
    checkResponseStatus(response);
    if (options.expectedType) {
      checkResponseType(response, options.expectedType);
    }
    return response;
  };
}

function useAddHeaders() {
  const { account } = useAuthState();
  const { season } = useSeason();
  return (headers: HeadersInit | undefined) => {
    const newHeaders = new Headers(headers);
    if (account.token) {
      newHeaders.set('X-Auth-Token', account.token);
    }
    if (season) {
      newHeaders.set('X-Season-ID', season.id);
    }
    return newHeaders;
  };
}

function toRequest(url: string | URL, init: ReqInit = {}): Request {
  const headers = new Headers(init.headers);
  const method = init.method ?? (init.body === undefined ? 'GET' : 'POST');
  const body = init.body === undefined ? undefined : JSON.stringify(init.body);
  if (body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  return new Request(url, { ...init, method, headers, body });
}

function checkResponseStatus(response: Response) {
  if (!response.ok) {
    const status = response.status;
    throw new HttpStatusError(
      status,
      t('Unexpected response status from server: {status}', { status }),
    );
  }
}

function checkResponseType(response: Response, expectedType: string) {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.toLowerCase().split(';')[0] !== expectedType) {
    throw new Error(
      t('Unexpected response type from server: {contentType}', { contentType }),
    );
  }
}

export class HttpStatusError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }

  get name() {
    return this.constructor.name;
  }

  get [Symbol.toStringTag]() {
    return this.constructor.name;
  }
}

export function is404(error: unknown): error is HttpStatusError {
  return error instanceof HttpStatusError && error.status === 404;
}
